/**
 * Merge freshly fetched TMDB seasons into existing admin data.
 * - Keeps every existing season (even if TMDB omits it).
 * - For matching season/episode numbers: keeps videoUrl, videoCode (and duration when video exists).
 * - Adds new episodes from TMDB without removing old ones.
 */
export function mergeTmdbSeasonsIntoExisting(existingSeasons = [], tmdbSeasons = []) {
  const seasonMap = new Map();

  for (const s of existingSeasons) {
    const sn = s.seasonNumber;
    seasonMap.set(sn, {
      seasonNumber: sn,
      episodes: (s.episodes || []).map((e) => ({ ...e })),
    });
  }

  for (const fs of tmdbSeasons) {
    const sn = fs.seasonNumber;
    let target = seasonMap.get(sn);
    if (!target) {
      target = { seasonNumber: sn, episodes: [] };
      seasonMap.set(sn, target);
    }

    const epMap = new Map();
    for (const e of target.episodes) {
      epMap.set(e.episodeNumber, { ...e });
    }

    for (const fe of fs.episodes || []) {
      const en = fe.episodeNumber;
      const old = epMap.get(en);
      if (!old) {
        epMap.set(en, { ...fe });
        continue;
      }

      const hasVideo =
        !!(old.videoCode && String(old.videoCode).trim()) ||
        !!(old.videoUrl && String(old.videoUrl).trim());

      epMap.set(en, {
        ...fe,
        title: fe.title || old.title,
        description: fe.description !== undefined ? fe.description : old.description,
        videoUrl: hasVideo ? old.videoUrl : fe.videoUrl || '',
        videoCode: hasVideo ? old.videoCode : fe.videoCode || '',
        duration:
          hasVideo && old.duration
            ? old.duration
            : fe.duration != null
              ? fe.duration
              : old.duration || 0,
        thumbnail: (fe.thumbnail && String(fe.thumbnail).trim()) || old.thumbnail || '',
      });
    }

    target.episodes = Array.from(epMap.values()).sort((a, b) => a.episodeNumber - b.episodeNumber);
  }

  return Array.from(seasonMap.values()).sort((a, b) => a.seasonNumber - b.seasonNumber);
}

export function countEpisodes(seasons = []) {
  return seasons.reduce((acc, s) => acc + (s.episodes?.length || 0), 0);
}
